<template>
    <div v-if="showToolbar">
        <v-app-bar height="64" app fixed flat color="bg" class="toolbar-container redesign-toolbar">
            <template v-if="titleToolbar">
                <div class="toolbar-heading">
                    <div class="toolbar-title-row">
                        <span class="toolbar-title">{{ titleToolbar.name }}</span>
                        <template v-if="showTutorial">
                            <button-tooltip @click="showTutorialDialog" icon small buttonClass="ml-1 tutorial-btn"
                                :tooltip="$t('tooltips.tutorials')">
                                <v-icon size="20" color="txtMuted">mdi-play-circle-outline</v-icon>
                            </button-tooltip>
                            <tutorials-dialog ref="tutorials" />
                            <coach-mark v-model="tutorialHint" target=".tutorial-btn" spotlight :blur="1"
                                :text="$t('common.tutorials_hint')" @dismiss="onTutorialHintDismiss" />
                        </template>
                    </div>
                    <span v-if="subtitle" class="toolbar-subtitle">{{ subtitle }}</span>
                </div>

                <v-spacer />

                <zapping-button v-if="isHome" class="mr-3" />

                <component v-if="titleToolbar.component" :is="titleToolbar.component" class="mr-2" />

                <theme-toggle />
            </template>
        </v-app-bar>
        <div class="toolbar-spacer-app" />
    </div>
</template>

<script>

import ConfigStudent from '@/components/common/toolbar/ConfigStudent'
import ConfigCoach from '@/components/common/toolbar/ConfigCoach'
import ConfigCompany from '@/components/common/toolbar/ConfigCompany'
import FilterCoach from '@/components/common/toolbar/FilterCoach'
import ShowStars from '@/components/common/toolbar/ShowStars'
import SetCoach from '@/components/common/toolbar/SetCoach'
import StudentsActions from '@/components/common/toolbar/StudentsActions';
import CompanyAdminActions from '@/components/common/toolbar/CompanyAdminActions';
import StatusBarManager from '@/utils/StatusBarManager';

import { mapActions, mapMutations, mapState } from "vuex";
import { tutorialsFrom } from '@/utils/Tutorials';
import TutorialsDialog from './TutorialsDialog.vue'
import CoachMark from './CoachMark.vue'
import ButtonTooltip from './ButtonTooltip.vue'
import ThemeToggle from './ThemeToggle.vue'
import ZappingButton from './ZappingButton.vue'

const PERSIST_TUTORIAL_HINT = true

export default {
    name: "NewToolbar",
    components: {
        ConfigStudent,
        ConfigCoach,
        ConfigCompany,
        FilterCoach,
        ShowStars,
        SetCoach,
        StudentsActions,
        CompanyAdminActions,
        TutorialsDialog,
        CoachMark,
        ButtonTooltip,
        ThemeToggle,
        ZappingButton,
    },
    props: {
        showToolbar: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            tutorialHint: false,
            tutorialHintTimer: null,
        }
    },
    computed: {
        ...mapState(['showCalendar', 'currentRoutine', 'title', 'config', 'toolbarMeta', 'toolbarSubtitle']),
        ...mapState({ tutorialsConfig: 'tutorials' }),
        color(vm = this) {
            const theme = vm.$vuetify.theme
            return theme.themes[theme.dark ? 'dark' : 'light'].bg
        },
        routeName(vm = this) {
            return vm.$route.name
        },
        isDark(vm = this) {
            return vm.$vuetify.theme.dark
        },
        isHome(vm = this) {
            return ['coach.home', 'company.home'].includes(vm.routeName)
        },
        subtitle(vm = this) {
            if (vm.toolbarSubtitle) return vm.toolbarSubtitle
            if (vm.isHome) {
                const date = vm.$moment().format('dddd D MMMM')
                const capitalized = date.charAt(0).toUpperCase() + date.slice(1)
                return `${capitalized} · ${vm.$t('common.week')} ${vm.$moment().isoWeek()}`
            }
            if (vm.toolbarMeta) {
                return vm.$t('common.students_subtitle', {
                    total: vm.toolbarMeta.total,
                    active: vm.toolbarMeta.active
                })
            }
            return ''
        },
        dark(vm = this) {
            let routes = [
                'splash',
                'coach.calendar.assignedRoutine',
                'student.coach-profile',
                'student.routine.detail',
                'student.routine.competence.detail',
                'student.invitations'
            ]
            return routes.includes(vm.routeName)
        },
        customTitle(vm = this) {
            if (vm.title) return vm.$te(vm.title) ? vm.$t(vm.title) : vm.title
            return vm.currentRoutine?.name || ''
        },
        routesInfo(vm = this) {
            return {
                // COACHES
                // COACH HOME
                'coach.home': { name: vm.$t('titles.home'), icon: '$home' },
                // COACH STUDENTS
                'coach.students': { name: vm.$t('titles.coach.students'), icon: '$account_multiple', component: 'StudentsActions' },
                'coach.students.chats': { name: vm.$t('titles.coach.chats'), icon: '$account_multiple' },
                'coach.students.profile': { name: vm.$t('titles.coach.student'), icon: '$account' },
                // COACH LIBRARY
                'coach.library': { name: vm.$t('titles.coach.library'), icon: '$training' },
                // COACH BLOCKS
                // COACH ROUTINES
                'coach.routines.detail': { name: vm.$t('titles.coach.routine.detail'), icon: '$training' },
                'coach.routines.edit': { name: vm.customTitle, icon: '$training' },
                'coach.routines.edit.assigned': { name: vm.$t('titles.coach.routine.edit_assigned'), icon: '$training' },
                // COACH PROGRAMS
                'coach.routines.program.detail': { name: vm.$t('titles.coach.programs.detail'), icon: '$training' },
                'coach.routines.program.detail.routine': { name: vm.$t('titles.coach.routine.detail'), icon: '$training' },
                // COACH ZAPPING
                'coach.zapping': { name: vm.$t('titles.zapping'), icon: '$training' },
                // COACH CALENDAR
                'coach.calendar': { name: vm.$t('titles.coach.calendar'), icon: '$calendar' },
                'coach.calendar.assignedRoutine': { name: vm.customTitle, icon: '$calendar' },
                // COACH STATISTICS
                'coach.statistics': { name: vm.$t('titles.coach.statistics'), icon: '$statistics' },
                // COACH EVALUATIONS
                'coach.evaluations': { name: vm.$t('titles.coach.evaluations'), icon: 'mdi-book-open-variant' },
                'coach.evaluations.statistics': { name: vm.$t('titles.coach.evaluation_statistics'), icon: 'mdi-book-open-variant' },
                // PROFILE
                'coach.profile': { name: vm.$t('titles.coach.profile'), icon: '$account_circle', component: 'ConfigCoach' },
                'coach.payment.edit': { name: vm.$t('titles.coach.payment_method_edit'), icon: '$account_circle' },
                // STUDENTS
                // STUDENT HOME
                'student.home': { name: vm.$t('titles.home'), icon: '$home' },
                'student.shifts': { name: vm.$t('titles.student.shifts'), icon: '$calendar' },
                // STUDENT COACHES
                'student.coaches': { name: vm.$t('titles.student.coaches'), icon: '$account_multiple', component: 'FilterCoach' },
                'student.coach-profile': { name: vm.$t('titles.student.coach'), icon: '$account_multiple', component: 'ShowStars' },
                // STUDENT CHATS
                'student.chats': { name: vm.$t('titles.student.chats'), icon: '$account_multiple' },
                // STUDENT ROUTINES
                'student.routines': { name: vm.$t('titles.student.routines'), icon: '$training' },
                'student.routine.detail': { name: vm.customTitle, icon: '$training' },
                'student.routine.competence.detail': { name: vm.customTitle, icon: '$training' },
                // STUDENT STATISTICS
                'student.statistics': { name: vm.$t('titles.student.statistics'), icon: '$statistics' },
                'student.habits': { name: vm.$t('titles.student.habits'), icon: 'mdi-checkbox-marked-outline' },
                // STUDENT PROFILE
                'student.profile': { name: vm.$t('titles.student.profile'), icon: '$account_circle', component: 'ConfigStudent' },
                // STUDENT INVITATIONS
                'student.invitations': { name: vm.$t('titles.student.invitations'), icon: '$account_circle' },
                // COMPANY
                'company.home': { name: vm.$t('titles.home'), icon: '$home' },
                // COMPANY ADMIN
                'company.admin': { name: vm.$t('titles.company.admin'), icon: '$company', component: 'CompanyAdminActions' },
                // COMPANY LIBRARY
                'company.library': { name: vm.$t('titles.coach.library'), icon: '$training', component: 'SetCoach' },
                // COMPANY BLOCKS
                // COACH ZAPPING
                'company.zapping': { name: vm.$t('titles.zapping'), icon: '$training' },
                // COMPANY ROUTINES
                'company.routines.detail': { name: vm.$t('titles.coach.routine.detail'), icon: '$training' },
                'company.routines.edit': { name: vm.customTitle, icon: '$training' },
                'company.routines.edit.assigned': { name: vm.$t('titles.coach.routine.edit_assigned'), icon: '$training' },
                // COMPANY PROGRAMS
                'company.routines.program.detail': { name: vm.$t('titles.coach.programs.detail'), icon: '$training' },
                'company.routines.program.detail.routine': { name: vm.$t('titles.coach.routine.detail'), icon: '$training' },
                // COMPANY CALENDAR
                'company.calendar': { name: vm.$t('titles.coach.calendar'), icon: '$calendar', component: 'SetCoach' },
                'company.calendar.assignedRoutine': { name: vm.customTitle, icon: '$calendar' },
                // COMPANY STATISTICS
                'company.statistics': { name: vm.$t('titles.coach.statistics'), icon: '$statistics', component: 'SetCoach' },
                // COMPANY BUSINESS
                'company.business': { name: vm.$t('titles.company.business'), icon: 'mdi-briefcase-outline' },
                // COMPANY EVALUATIONS
                'company.evaluations': { name: vm.$t('titles.coach.evaluations'), icon: 'mdi-book-open-variant', component: 'SetCoach' },
                'company.evaluations.statistics': { name: vm.$t('titles.coach.evaluation_statistics'), icon: 'mdi-book-open-variant' },
                // COMPANY PROFILE
                'company.profile': { name: vm.$t('titles.company.profile'), icon: '$account_circle', component: 'ConfigCompany' },
                'company.payment.edit': { name: vm.$t('titles.coach.payment_method_edit'), icon: '$account_circle' },
                // COMPANY COACH PROFILE
                'company.coaches.profile': { name: vm.$t('titles.company.coach_profile'), icon: '$company' },
                // COMPANY STUDENT PROFILE
                'company.students.profile': { name: vm.$t('titles.company.student_profile'), icon: '$company' },
                // ADMIN
                'admin.home': { name: 'Panel de administración', icon: 'mdi-shield-account-outline' },
                'admin.confirm-email': { name: 'Confirmar email', icon: 'mdi-email-check-outline' },
                'admin.delete-user': { name: 'Borrar usuario', icon: 'mdi-account-remove-outline' },
                'admin.restore-user': { name: 'Restaurar cuenta', icon: 'mdi-account-reactivate-outline' },
                'admin.user-access': { name: 'Accesos de un usuario', icon: 'mdi-key-outline' },
                'admin.user-detail': { name: 'Ficha de usuario', icon: 'mdi-card-account-details-outline' },
                'admin.company-to-coach': { name: 'Empresa a entrenador', icon: 'mdi-office-building-remove-outline' },
                'admin.invitations': { name: 'Invitaciones', icon: 'mdi-email-fast-outline' },
                'admin.errors': { name: 'Errores', icon: 'mdi-bug-outline' },
                'admin.config': { name: 'Configuración global', icon: 'mdi-tune' },
                'admin.tutorials': { name: 'Tutoriales', icon: 'mdi-play-circle-outline' },
                'admin.attach-users': { name: 'Enlazar usuarios', icon: 'mdi-account-multiple-plus-outline' },
                'admin.excel': { name: 'Subir ejercicios', icon: 'mdi-file-upload-outline' },
                'admin.surveys': { name: 'Encuestas', icon: 'mdi-poll' },
            }
        },
        titleToolbar(vm = this) {
            return vm.routesInfo[vm.routeName]
        },
        isTutorialActive(vm = this) {
            return vm.tutorialsConfig?.active === true
        },
        showTutorial(vm = this) {
            if (!vm.isTutorialActive) return false
            return tutorialsFrom(vm.tutorialsConfig, { screen: vm.tutorialScreen, accountType: vm.accountType }).length > 0
        },
        tutorialScreen(vm = this) {
            const name = vm.routeName || ''
            if (name.startsWith('admin.')) return ''
            return name.split('.')[1] || ''
        },
        tutorialHintKey(vm = this) {
            return vm.account?.id ? `builder.tutorialsHint.${vm.account.id}` : null
        }
    },
    async mounted() {
        const vm = this;
        vm.onChangeColor()
        if (!vm.config) await vm.bindConfig()
        await vm.fetchTutorials()
        vm.maybeShowTutorialHint()
    },
    beforeDestroy() {
        clearTimeout(this.tutorialHintTimer)
    },
    methods: {
        ...mapMutations(['setShowCalendar']),
        ...mapActions(['bindConfig', 'fetchTutorials']),
        goBack() {
            const vm = this
            vm.$router.go(-1)
        },
        hideCalendar() {
            const vm = this
            vm.setShowCalendar(false)
        },
        onChangeColor() {
            const vm = this;
            StatusBarManager.setBase({ style: vm.isDark ? 'DARK' : 'LIGHT', color: vm.color })
        },
        showTutorialDialog() {
            const vm = this;
            vm.$refs.tutorials.show()
        },
        maybeShowTutorialHint() {
            const vm = this;
            if (vm.tutorialHint || !vm.showTutorial || !vm.isHome || !vm.tutorialHintKey) return
            let seen = true
            try { seen = localStorage.getItem(vm.tutorialHintKey) === '1' } catch (e) { seen = true }
            if (seen) return
            clearTimeout(vm.tutorialHintTimer)
            vm.tutorialHintTimer = setTimeout(() => { vm.tutorialHint = true }, 1200)
        },
        onTutorialHintDismiss() {
            const vm = this;
            if (!PERSIST_TUTORIAL_HINT || !vm.tutorialHintKey) return
            try { localStorage.setItem(vm.tutorialHintKey, '1') } catch (e) { /* noop */ }
        }
    },
    watch: {
        color() {
            this.onChangeColor()
        },
        showTutorial() {
            this.maybeShowTutorialHint()
        },
        isHome() {
            this.maybeShowTutorialHint()
        },
        tutorialHintKey() {
            this.maybeShowTutorialHint()
        }
    }
}
</script>

<style scoped>

.redesign-toolbar ::v-deep .v-toolbar__content {
    padding-left: 28px;
    padding-right: 28px;
    padding-top: 16px;
    padding-bottom: 16px;
}

@media (max-width: 599px) {
    .redesign-toolbar ::v-deep .v-toolbar__content {
        padding-left: 16px;
        padding-right: 16px;
    }
}

.tutorial-btn ::v-deep .v-icon {
    transition: color .15s ease;
}

.tutorial-btn:hover ::v-deep .v-icon {
    color: var(--v-txtPrimary-base) !important;
}

.toolbar-heading {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
}

.toolbar-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.toolbar-title {
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--v-txtPrimary-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.toolbar-subtitle {
    font-size: 13px;
    color: var(--v-txtSecondary-base);
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

</style>